import { motion } from "framer-motion";
import { Pencil, CheckCircle2 } from "lucide-react";
import { SchoolData } from "@/lib/onboarding-store";

interface SchoolStepReviewProps {
  data: Partial<SchoolData>;
  onConfirm: () => void;
  onEditStep: (step: number) => void;
}

interface SectionCardProps {
  index: number;
  emoji: string;
  title: string;
  step: number;
  onEdit: (step: number) => void;
  children: React.ReactNode;
}

function SectionCard({ index, emoji, title, step, onEdit, children }: SectionCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05 * index, ease: "easeOut" }}
      className="bg-white border border-border rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <div className="flex items-start gap-4 p-5">
        {/* Left: emoji + title */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl leading-none" aria-hidden="true">{emoji}</span>
            <h3 className="font-semibold text-foreground text-sm tracking-wide uppercase">{title}</h3>
          </div>
          <div className="space-y-1.5">{children}</div>
        </div>

        {/* Right: edit button */}
        <button
          onClick={() => onEdit(step)}
          aria-label={`Edit ${title}`}
          className="flex-shrink-0 p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-accent transition-colors duration-150 mt-0.5"
        >
          <Pencil size={15} strokeWidth={2} />
        </button>
      </div>
    </motion.div>
  );
}

function DataRow({ label, value, missing }: { label: string; value?: string | number | null; missing?: string }) {
  const display = value !== undefined && value !== null && value !== "" ? String(value) : null;
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="text-muted-foreground shrink-0">{label}:</span>
      {display ? (
        <span className="text-foreground font-medium truncate">{display}</span>
      ) : (
        <span className="text-muted-foreground italic">{missing ?? "Not provided"}</span>
      )}
    </div>
  );
}

function Pill({ children, muted }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        muted ? "bg-secondary text-muted-foreground" : "bg-accent text-primary"
      }`}
    >
      {children}
    </span>
  );
}

export default function SchoolStepReview({ data, onConfirm, onEditStep }: SchoolStepReviewProps) {
  const {
    schoolName,
    postcode,
    phone,
    contactFirstName,
    contactLastName,
    contactTitle,
    contactEmail,
    contactPhone,
    secondaryContacts,
    staff,
    policies,
    subjects,
    schoolTypes,
    periodConfig,
    guestAccount,
  } = data;

  const fullContactName =
    [contactFirstName, contactLastName].filter(Boolean).join(" ") || null;

  const secondaryCount = secondaryContacts?.length ?? 0;
  const staffCount = staff?.length ?? 0;
  const subjectCount = subjects?.length ?? 0;
  const policyCount = policies?.length ?? 0;
  const numberOfPeriods = periodConfig?.numberOfPeriods ?? null;
  const registrationEnabled = periodConfig?.registration?.enabled ?? false;
  const firstLessonStart = periodConfig?.schoolStartTime ?? null;
  const breakAfterPeriod = periodConfig?.breakAfterPeriod ?? null;
  const breakDuration = periodConfig?.breakDurationMins ?? null;
  const lunchAfterPeriod = periodConfig?.lunchAfterPeriod ?? null;
  const lunchDuration = periodConfig?.lunchDurationMins ?? null;
  const schoolTypesDisplay =
    schoolTypes && schoolTypes.length > 0 ? schoolTypes.join(", ") : null;
  const hasGuestAccount = !!(guestAccount?.username);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _hasPassword = true; // Step 3 is password — if user reached review, it's set

  return (
    <div className="min-h-screen bg-background paper-dots flex flex-col">
      {/* Header */}
      <div className="px-6 pt-12 pb-8 text-center max-w-2xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-accent mb-5">
            <CheckCircle2 size={24} className="text-primary" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Almost there! Review your details
          </h1>
          <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mx-auto">
            Check everything looks right before we create your account.
          </p>
        </motion.div>
      </div>

      {/* Cards */}
      <div className="flex-1 px-4 pb-6 max-w-2xl mx-auto w-full space-y-3">

        {/* 1. School Info */}
        <SectionCard index={0} emoji="🏫" title="School Info" step={1} onEdit={onEditStep}>
          <DataRow label="School name" value={schoolName} />
          <DataRow label="Postcode" value={postcode} />
          <DataRow label="Phone" value={phone} />
        </SectionCard>

        {/* 2. Main Contact */}
        <SectionCard index={1} emoji="👤" title="Main Contact" step={2} onEdit={onEditStep}>
          <DataRow label="Name" value={fullContactName} />
          <DataRow label="Title" value={contactTitle} />
          <DataRow label="Email" value={contactEmail} />
          <DataRow label="Phone" value={contactPhone} />
          {secondaryCount > 0 && (
            <div className="pt-1">
              <Pill>{secondaryCount} additional contact{secondaryCount !== 1 ? "s" : ""}</Pill>
            </div>
          )}
        </SectionCard>

        {/* 3. Password */}
        <SectionCard index={2} emoji="🔑" title="Password" step={3} onEdit={onEditStep}>
          <div className="flex items-baseline gap-2 text-sm">
            <span className="text-muted-foreground shrink-0">Password:</span>
            <span className="text-foreground font-medium tracking-widest">••••••••</span>
            <Pill>Set</Pill>
          </div>
        </SectionCard>

        {/* 4. Configuration */}
        <SectionCard index={3} emoji="⚙️" title="Configuration" step={4} onEdit={onEditStep}>
          <DataRow label="School type" value={schoolTypesDisplay} missing="Not specified" />
          {numberOfPeriods !== null && (
            <DataRow
              label="School day"
              value={[
                `${numberOfPeriods} period${numberOfPeriods !== 1 ? "s" : ""}`,
                firstLessonStart ? `first lesson at ${firstLessonStart}` : null,
                breakAfterPeriod ? `break after P${breakAfterPeriod}${breakDuration ? ` (${breakDuration} min)` : ""}` : null,
                lunchAfterPeriod ? `lunch after P${lunchAfterPeriod}${lunchDuration ? ` (${lunchDuration} min)` : ""}` : null,
              ].filter(Boolean).join(" · ")}
            />
          )}
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            <Pill muted={!registrationEnabled}>
              Registration {registrationEnabled ? "on" : "off"}
            </Pill>
            {hasGuestAccount && <Pill>Guest login saved</Pill>}
          </div>
          {subjectCount > 0 && (
            <div className="pt-1 space-y-1">
              <span className="text-xs text-muted-foreground">Subjects:</span>
              <div className="flex flex-wrap gap-1">
                {(subjects ?? []).slice(0, 8).map(s => <Pill key={s}>{s}</Pill>)}
                {subjectCount > 8 && <Pill muted>+{subjectCount - 8} more</Pill>}
              </div>
            </div>
          )}
          {subjectCount === 0 && <Pill muted>No subjects added</Pill>}
        </SectionCard>

        {/* 5. Staff */}
        <SectionCard index={4} emoji="👥" title="Staff" step={5} onEdit={onEditStep}>
          {staffCount > 0 ? (
            <div className="flex items-center gap-2">
              <Pill>{staffCount} teacher{staffCount !== 1 ? "s" : ""} added</Pill>
            </div>
          ) : (
            <div className="flex items-baseline gap-2 text-sm">
              <span className="text-muted-foreground italic">No teachers added yet</span>
              <span className="text-xs text-muted-foreground">(you can invite later)</span>
            </div>
          )}
        </SectionCard>

        {/* 6. Policies */}
        <SectionCard index={5} emoji="📋" title="Policies" step={6} onEdit={onEditStep}>
          {policyCount > 0 ? (
            <div className="flex items-center gap-2">
              <Pill>{policyCount} cover polic{policyCount !== 1 ? "ies" : "y"} added</Pill>
            </div>
          ) : (
            <div className="flex items-baseline gap-2 text-sm">
              <span className="text-muted-foreground italic">No policy added</span>
              <span className="text-xs text-muted-foreground">(you can add one later)</span>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Footer */}
      <div className="px-4 pt-2 pb-10 max-w-2xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.35, ease: "easeOut" }}
          className="space-y-4"
        >
          <button
            onClick={onConfirm}
            className="w-full py-3.5 px-6 rounded-xl bg-primary hover:bg-primary/90 active:bg-primary/80 text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Confirm &amp; Create Account
          </button>

          <p className="text-center text-xs text-muted-foreground leading-relaxed">
            You can always update these details from your school settings after setup.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
